import React, {useEffect, useState} from 'react';
import {Box, Checkbox, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useUser from '../../../../utility/hooks/useUser';
import useNotification from '../../../../utility/hooks/useNotification';
import {
    useGetSchoolQuery,
    useUpdateSimsCommunicationsPermissionsMutation
} from '../../../../api/services/schoolManagementService';

// AD-2697: the SIMS boxes the school asks Admicity to tick for parents eligible for an Admicity account.
export const SIMS_PERMISSION_ROWS = [
    ['InTouch Communication', 'simsTickInTouchApplicants', 'simsTickInTouchStudents'],
    ['Pupil Report', 'simsTickPupilReportApplicants', 'simsTickPupilReportStudents'],
    ['Correspondence', 'simsTickCorrespondenceApplicants', 'simsTickCorrespondenceStudents'],
];
const FIELDS = SIMS_PERMISSION_ROWS.flatMap(([, applicants, students]) => [applicants, students]);
const fromSchool = (school) => Object.fromEntries(FIELDS.map(field => [field, !!school?.[field]]));

const SimsCommunicationsPermissions = () => {
    const {user} = useUser();
    const {showSuccessNotification, showErrorNotification} = useNotification();
    const {data: school} = useGetSchoolQuery(user.schoolId, {skip: !user.schoolId});
    const [update, {isLoading}] = useUpdateSimsCommunicationsPermissionsMutation();
    const [values, setValues] = useState(fromSchool(school));

    useEffect(() => setValues(fromSchool(school)), [school]);

    const toggle = async (field, checked) => {
        const previous = values;
        const next = {...values, [field]: checked};
        setValues(next);
        try {
            await update(next).unwrap();
            showSuccessNotification('SIMS Communications Permissions saved');
        } catch {
            setValues(previous);
            showErrorNotification('SIMS Communications Permissions could not be saved. Please try again.');
        }
    };

    return (
        <Box sx={{mt: 3}} data-testid="sims-communications-permissions">
            <Stack direction="row" alignItems="center" sx={{mb: 1}}>
                <Typography variant="h6" fontWeight="bold">SIMS Communications Permissions</Typography>
                <Tooltip
                    arrow
                    title="If parent invitations have not been enabled for applicants or students, this information cannot be synced."
                >
                    <IconButton size="small" aria-label="About SIMS Communications Permissions" sx={{ml: 0.5}}>
                        <InfoOutlinedIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                Other third-party applications require certain permission boxes to be ticked to allow them to sync with
                SIMS. Admicity can tick these boxes in SIMS but it does not require them for its own integration. Please
                tick any SIMS settings you would like Admicity to update.
            </Typography>
            <Box
                component="table"
                sx={{
                    borderCollapse: 'collapse',
                    '& th, & td': {py: 0, px: 1, fontSize: '0.875rem'},
                    '& th': {fontWeight: 600, textAlign: 'center'},
                    '& td': {textAlign: 'center'},
                    '& td:first-of-type': {textAlign: 'left', pl: 0, pr: 2},
                }}
            >
                <thead>
                    <tr>
                        <th/>
                        <th>Applicants</th>
                        <th>Students</th>
                    </tr>
                </thead>
                <tbody>
                    {SIMS_PERMISSION_ROWS.map(([label, applicants, students]) => (
                        <tr key={label}>
                            <td>{label}</td>
                            {[applicants, students].map(field => (
                                <td key={field}>
                                    <Checkbox
                                        size="small"
                                        sx={{p: 0.5}}
                                        checked={!!values[field]}
                                        disabled={isLoading || !school}
                                        onChange={(event) => toggle(field, event.target.checked)}
                                        inputProps={{
                                            'aria-label': `${label} for ${field.endsWith('Applicants') ? 'applicants' : 'students'}`
                                        }}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );
};

export default SimsCommunicationsPermissions;
